import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { userInfoSelectors } from '@panel/core/src/state/ducks/user';
import { bumexFeatureSelectors } from '@panel/core/src/state/ducks/bumexFeatures';
import { selectColor } from '@panel/commons';
import noImage from '@panel/commons/assets/icons/aviso-sin-foto-detalle.svg';
import Drawer from '@panel/commons/lib/Drawer';
import { getLinkToEdit } from '@panel/core/src/utils/getPublisherLinks';
import { PostingsOperations } from '../../../../ducks/posting';
import DetailHeader from './DetailHeader';
import MetricsSection from './MetricsSection';
import ActionsSection from './ActionsSection';
import { ReservedTag } from './styles';

const siteId = process.env.REACT_APP_BUILD_SITE;

function PostingDetails({ posting, onClose, userCanEditPermissions, publisherTypeId, finishReason }) {
  const [postIndex, setPostIndex] = useState(null);
  const { t } = useTranslation();
  const {
    title,
    urlPicture,
    urlPosting,
    pricesOperationTypes,
    stateAndDates,
    postingLocation,
    internalCode,
    postingId,
    publicationPlan,
    isReservado,
    realEstateType,
    postingIndex,
    publicationType,
    quality,
    qualityStatus,
    performanceStatus,
    daysLeftToQuality,
    validations,
    isDuplicate,
    isPenalized,
    postingReports,
    postingLogs,
    publicationPlanId,
    finishReasonByPosting,
    loadingFinishReasonByPosting,
    finishReasonErrorByPosting
  } = posting;
  const dispatch = useDispatch();
  const { isSeeker, isIntegrator, isAssistant } = useSelector(userInfoSelectors.getuserInfoState);
  const totalReport = postingReports && postingReports.totalReport;
  const [showTips, setShowTips] = useState(false);
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);

  useEffect(() => {
    if (!postIndex) {
      setPostIndex(postingIndex);
    }
    dispatch(PostingsOperations.fetchIsComplete(postingId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId]);

  const location = postingLocation && postingLocation[0];
  const dates = stateAndDates && stateAndDates[0];
  const operationType = posting.pricesOperationTypes[0] && posting.pricesOperationTypes[0].operationType;
  const canUpgradePlan = posting.hasLinkUpgradePlan;
  const { status } = stateAndDates[0] || [''];
  const isDraft = status === 'DRAFT';
  const isDeleted = status === 'DELETED';
  const isFinalized = status === 'OFFLINE';
  const isDevelopment = publicationType !== 'Clasificado';
  const userIsParticular = publisherTypeId === '1' || isSeeker;
  const linkToDesempeno = `../panel/desempeno?postingId=${postingId}`;

  useEffect(() => {
    if ((isDeleted || isFinalized) && !loadingFinishReasonByPosting && !finishReasonErrorByPosting)
      dispatch(PostingsOperations.getFinishReasonByPosting(postingId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkUpgradePlan = `../panel/publicador/planes/seleccion?postingId=${postingId}`;

  const isFreePlan = publicationPlan && publicationPlan.includes('FREE');

  const particularNotFree = !isFreePlan && publisherTypeId === '1';

  const [copyFeedbackMessage, setCopyFeedbackMessage] = useState('');
  const [copyTimeoutRef, setCopyTimeOutRef] = useState('');

  const showCopyFeedbackMessage = message => {
    setCopyFeedbackMessage(message);
    clearTimeout(copyTimeoutRef);
    setCopyTimeOutRef(
      setTimeout(() => {
        setCopyFeedbackMessage('');
      }, 2000)
    );
  };

  const showDesempeno = !isDraft;
  const qualityNotApply = qualityStatus === 'not_apply';

  const openModal = e => {
    e.stopPropagation();
    setShowTips(!showTips);
  };

  return (
    <Drawer isOpen={posting} onClose={onClose} isDarkBackground>
      {isReservado && (
        <ReservedTag type='isDetailCard' data-panel='posting-tagReservado'>
          Reservado
        </ReservedTag>
      )}
      <PictureContainer>
        {urlPicture ? (
          <Picture urlPicture={urlPicture} data-panel='posting-img' />
        ) : (
          <WithoutPicture data-panel='posting-img-sinImagen'>
            <img src={noImage} alt='Aviso sin fotos' />
            <span>{t('postings.card.info.withoutPicture')}</span>
          </WithoutPicture>
        )}
        <span />
      </PictureContainer>
      <InfoContainer>
        <DetailHeader
          priceAndOperation={pricesOperationTypes}
          propertyType={realEstateType}
          status={dates && dates.status}
          title={title}
          address={location && location.address}
          city={location && `${location.zone}, ${location.city}`}
          totalReport={totalReport}
          postingId={postingId}
          postingReports={postingReports}
          isReservado={isReservado}
          linkToEdit={getLinkToEdit(userIsParticular, bumexFeatures, postingId, isDevelopment)}
          quality={quality}
          qualityNotApply={qualityNotApply}
          isDuplicate={isDuplicate}
          isPenalized={isPenalized}
          qualityStatus={qualityStatus}
          validations={validations}
          openModal={openModal}
          showTips={showTips}
          isIntegrator={isIntegrator}
          isAssistant={isAssistant}
          isParticular={userIsParticular}
        />

        <MetricsSection
          performanceStatus={performanceStatus}
          daysLeftToQuality={daysLeftToQuality}
          linkToDesempeno={linkToDesempeno}
          allowRedirectDesempeno={showDesempeno}
          internalCode={internalCode}
          postingId={postingId}
          dates={dates}
          linkUpgradePlan={linkUpgradePlan}
          canUpgradePlan={canUpgradePlan}
          showCopyFeedbackMessage={showCopyFeedbackMessage}
          validations={validations}
          publicationType={publicationType}
          isDuplicate={isDuplicate}
          isParticular={userIsParticular}
          posting={posting}
          showTips={showTips}
          setShowTips={setShowTips}
          isIntegrator={isIntegrator}
          publisherTypeId={publisherTypeId}
          isAssistant={isAssistant}
        />
        <ActionsSection
          status={dates && dates.status}
          postingId={postingId}
          urlPosting={urlPosting}
          publicationPlan={publicationPlan}
          pricesOperationTypes={pricesOperationTypes}
          postingIndex={postIndex}
          publicationType={publicationType}
          operationType={operationType}
          isReservado={isReservado}
          userCanEditPermissions={userCanEditPermissions}
          particularNotFree={particularNotFree}
          publisherTypeId={publisherTypeId}
          showDesempeno={showDesempeno}
          linkToEdit={getLinkToEdit(userIsParticular, bumexFeatures, postingId, isDevelopment)}
          postingLogs={postingLogs}
          posting={posting}
          isAssistant={isAssistant}
          finishReason={finishReason}
          publicationPlanId={publicationPlanId}
          siteId={siteId}
          finishReasonByPosting={finishReasonByPosting}
        />
      </InfoContainer>
      {copyFeedbackMessage && <CopyFeedback>{copyFeedbackMessage}</CopyFeedback>}
    </Drawer>
  );
}

const InfoContainer = styled.div`
  z-index: 1;
  margin: 0 0 26px 0;
`;

const PictureContainer = styled.div`
  position: relative;
  width: 100%;
  height: fit-content;
  > span {
    width: 100%;
    height: 4rem;
    position: absolute;
    bottom: -2rem;
    background-color: white;
    border-radius: 20px;
  }
`;

const PictureLayout = styled.div`
  height: 250px;
  width: 100%;
  background-color: ${selectColor('grayMediumLight')};
`;

const Picture = styled(PictureLayout)`
  ${({ urlPicture }) =>
    urlPicture !== '' &&
    css`
      background: url(${urlPicture.replace('/100x75/', '/300x225/')}) no-repeat;
      background-position: center;
      background-size: cover;
    `};
`;

const WithoutPicture = styled(PictureLayout)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  > img {
    margin-bottom: 8px;
  }
  > span {
    color: ${selectColor('grayDark')};
    font-size: 14px;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    bottom: -48px;
  }
  to {
    opacity: 1;
    bottom: 8px;
  }
`;

const CopyFeedback = styled.div`
  width: -webkit-fill-available;
  width: -moz-available;
  width: fill-available;
  height: 50px;
  position: fixed;
  bottom: 8px;
  border-radius: 4px;
  background-color: black;
  color: white;
  font-size: 14px;
  font-weight: 400;
  display: flex;
  align-items: center;
  z-index: 1;
  padding-left: 14px;
  transition: opacity 0.5s;
  animation: ${fadeIn} 0.5s ease-out forwards;
  opacity: 1;
  margin: 0 8px;
`;

export default PostingDetails;
